/*
 ** method **
 *  add / subtract / multiply /divide
 *
 * ** explame **
 *  0.1 + 0.2 == 0.30000000000000004 （多了 0.00000000000004）
 *  0.2 + 0.4 == 0.6000000000000001  （多了 0.0000000000001）
 *  19.9 * 100 == 1989.9999999999998 （少了 0.0000000000002）
 *
 * floatObj.add(0.1, 0.2) >> 0.3
 * floatObj.multiply(19.9, 100) >> 1990
 *
 */
import { Decimal } from "decimal.js";

/*
 * 判断obj是否为一个整数
 */
const isInteger = (obj) => {
  return Math.floor(obj) === obj;
};
/*
 * 将一个浮点数转成整数，返回整数和倍数。如 3.14 >> 314，倍数是 100
 * @param floatNum {number} 小数
 * @return {object}
 *   {times:100, num: 314}
 */
const toInteger = (floatNum: number) => {
  const ret = {
    times: 1,
    num: 0,
  };
  if (isInteger(floatNum)) {
    ret.num = floatNum;
    return ret;
  }
  const strfi = floatNum + "";
  const dotPos = strfi.indexOf(".");
  const len = strfi.substr(dotPos + 1).length;
  const times = Math.pow(10, len);
  const intNum = Number(floatNum * times);
  ret.times = times;
  ret.num = intNum;
  return ret;
};

/*
 * 核心方法，实现加减乘除运算，确保不丢失精度
 * 思路：把小数放大为整数（乘），进行算术运算，再缩小为小数（除）
 *
 * @param a {number} 运算数1
 * @param b {number} 运算数2
 * @param digits {number} 精度，保留的小数点数，比如 2, 即保留为两位小数
 * @param op {string} 运算类型，有加减乘除（add/subtract/multiply/divide）
 *
 */
const operation = (a: number, b: number, digits: number, op: string) => {
  const o1 = toInteger(a);
  const o2 = toInteger(b);
  const n1 = o1.num;
  const n2 = o2.num;
  const t1 = o1.times;
  const t2 = o2.times;
  const max = t1 > t2 ? t1 : t2;
  let result = null;
  switch (op) {
    case "add":
      if (t1 === t2) {
        // 两个小数位数相同
        result = n1 + n2;
      } else if (t1 > t2) {
        // o1 小数位 大于 o2
        result = n1 + n2 * (t1 / t2);
      } else {
        // o1 小数位 小于 o2
        result = n1 * (t2 / t1) + n2;
      }
      return Number((result / max).toFixed(digits));
    case "subtract":
      if (t1 === t2) {
        result = n1 - n2;
      } else if (t1 > t2) {
        result = n1 - n2 * (t1 / t2);
      } else {
        result = n1 * (t2 / t1) - n2;
      }
      return Number((result / max).toFixed(digits));
    case "multiply":
      result = (n1 * n2) / (t1 * t2);
      return Number(result.toFixed(digits));
    case "divide":
      result = (n1 / n2) * (t2 / t1);
      return Number(result.toFixed(digits));
    default:
  }
};

// 加减乘除的四个接口
export const add = (a: number, b: number, digits: number) => {
  return operation(a, b, digits, "add");
};

export const subtract = (a: number, b: number, digits: number) => {
  return operation(a, b, digits, "subtract");
};

export const multiply = (a: number, b: number, digits: number) => {
  return operation(a, b, digits, "multiply");
};

export const divide = (a: number, b: number, digits: number) => {
  return operation(a, b, digits, "divide");
};

// 判断数值的小数位是否大于两位
export const hasMoreThanTwoDecimals = (num: number) => {
  if (Number.isNaN(Number(num))) {
    return false;
  }
  const str = Number(num).toString();
  const decimalPart = str.split('.')[1];
  return decimalPart ? decimalPart.length > 2 : false;
}

const gcdDecimal = (a: Decimal, b: Decimal): Decimal => {
  let x = a.abs();
  let y = b.abs();
  while (!y.isZero()) {
    const t = y;
    y = x.mod(y);
    x = t;
  }
  return x;
};

/**
 * 计算计价单位：使「单价 × 数量」最多两位小数的最小正整数数量。
 * 单价本身不超过两位小数时返回 1。
 */
export const calcQuantityStep = (price: number | string | null | undefined): number => {
  if (price === null || price === undefined || price === "") {
    return 1;
  }
  try {
    const d = new Decimal(price).abs();
    if (!d.isFinite() || d.isZero()) {
      return 1;
    }
    const times100 = d.mul(100);
    if (times100.isInteger()) {
      return 1;
    }
    const extraDp = times100.decimalPlaces();
    if (!extraDp) {
      return 1;
    }
    const denominator = new Decimal(10).pow(extraDp);
    const numerator = times100.mul(denominator);
    const step = denominator.div(gcdDecimal(numerator, denominator));
    return step.isInteger() ? step.toNumber() : 1;
  } catch (e) {
    return 1;
  }
};

export const formatPrice = (num: number) => {
  if (num == null) {
    return '';
  }
  const n = Number(num);
  if (Number.isNaN(n)) {
    return '';
  }
  // 最多保留 4 位，去掉末尾多余的 0，至少保留 2 位
  const fixed = n.toFixed(4);
  let result = fixed.replace(/0+$/, '').replace(/\.$/, '');
  const decimalPart = result.split('.')[1] || '';
  if (decimalPart.length < 2) {
    result = Number(result).toFixed(2);
  }
  return result;
}
